// cartUtils.js - Shopping Cart Utility Functions
// Handles localStorage-based cart management

export const getCart = () => {
  const cart = localStorage.getItem('cart');
  return cart ? JSON.parse(cart) : [];
};

export const saveCart = (cart) => {
  localStorage.setItem('cart', JSON.stringify(cart));
  window.dispatchEvent(new Event('cartUpdated'));
};

// Import products data for validation
import { products } from '../data/productsData';

/**
 * Add a product to cart with optional variant/size selection.
 * @param {Object} product - The product object
 * @param {Object|null} selectedVariant - The selected variant or size object { label, price }
 * @param {number} quantity - Quantity to add (default 1)
 */
export const addToCart = (productInput, variantInput = null, quantity = 1) => {
  const cart = getCart();

  // 1. Validate Product Exists in Source Validation
  const validProduct = products.find(p => p.id === productInput.id);
  if (!validProduct) {
    console.error('Invalid product');
    return cart;
  }

  // 2. Validate Variant/Size & Price Logic
  let validVariant = null;
  let finalPrice = validProduct.price; // Default fallback

  if (variantInput) {
    // Check if product supports variants/sizes
    const options = validProduct.variants || validProduct.sizes || [];
    validVariant = options.find(opt => opt.label === variantInput.label);

    if (validVariant) {
      finalPrice = validVariant.price;
    } else {
      console.warn('Invalid variant selected, falling back to base product');
    }
  }

  // Create a unique ID for the cart item (Frontend Key)
  let cartId = validProduct.id;
  if (validVariant) {
    const variantSlug = validVariant.label.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    cartId = `${validProduct.id}-${variantSlug}`;
  }

  const existingItemIndex = cart.findIndex(item => (item.cartId || item.id) === cartId);

  // Construct Standardized Item Payload
  const itemPayload = {
    cartId: cartId, // Frontend unique key
    product_id: validProduct.product_id, // REQUIRED: Backend Numeric Identity
    name: validProduct.name,
    variant_key: validVariant ? validVariant.label.toLowerCase().replace(/[^a-z0-9]+/g, '-') : null,
    variant_label: validVariant ? validVariant.label : null,
    price: finalPrice,
    image: validVariant?.image || validProduct.image,
    qty: quantity,
    freeShipping: validProduct.freeShipping || false, // For test products
    // Legacy support (optional, can be removed if strictly cleaning up)
    id: validProduct.id,
    type: validProduct.type
  };

  if (existingItemIndex > -1) {
    // Update existing item
    cart[existingItemIndex].qty += quantity; // Standardize on 'qty' vs 'quantity' - Backend wants 'qty', frontend used 'quantity'. I should probably support both or migrate to 'qty'.
    // CartUtils used 'quantity' before. I will write 'qty' but also keep 'quantity' for frontend compat if needed?
    // User request: "Ensure every cart item uses this structure ... qty: number".
    // So I will convert to `qty`.
    // Wait, the previous code used `quantity`. If I change it, I might break other components (MiniCartDrawer) reading `quantity`.
    // I will write both or ensure components read `qty`.
    // Let's check MiniCartDrawer later. For now, I'll update the object heavily.
    cart[existingItemIndex].qty = (cart[existingItemIndex].qty || 0) + quantity;
    cart[existingItemIndex].quantity = cart[existingItemIndex].qty; // Sync

    // Always update price to current source of truth
    cart[existingItemIndex].price = finalPrice;
    cart[existingItemIndex].image = validVariant?.image || validProduct.image;

    // Update structural fields just in case they were missing
    cart[existingItemIndex].product_id = validProduct.product_id;
    cart[existingItemIndex].variant_key = itemPayload.variant_key;
    cart[existingItemIndex].variant_label = itemPayload.variant_label;
  } else {
    // Add new item
    // Ensure 'quantity' is also present for compatibility if needed, but 'qty' is the target
    itemPayload.quantity = quantity;
    cart.push(itemPayload);
  }

  saveCart(cart);
  return cart;
};

export const removeFromCart = (cartId) => {
  const cart = getCart().filter(item => (item.cartId || item.id) !== cartId);
  saveCart(cart);
  return cart;
};

export const clearCart = () => {
  localStorage.removeItem('cart');
  window.dispatchEvent(new Event('cartUpdated'));
};

export const updateQuantity = (cartId, qty) => {
  if (qty < 1) {
    return removeFromCart(cartId);
  }
  const cart = getCart().map(item =>
    (item.cartId || item.id) === cartId ? { ...item, quantity: qty } : item
  );
  saveCart(cart);
  return cart;
};

export const getCartItemCount = () => {
  const cart = getCart();
  return cart.reduce((total, item) => total + item.quantity, 0);
};

// Helper function to extract or return numeric price
export const getNumericPrice = (priceInput) => {
  if (typeof priceInput === 'number') return priceInput;
  if (!priceInput) return 0;
  // Handle "₹ 100" or similar strings
  const numericValue = priceInput.toString().replace(/[^\d]/g, '');
  return parseInt(numericValue) || 0;
};


